import React, { useState } from 'react';
import Layout from '../../components/common/Layout';
import { Search, Filter, ShoppingBag } from 'lucide-react';
import { resources, Resource, ResourceType } from '../../constants/resources';
import ResourceCard from '../../components/resources/ResourceCard';
import ResourceDetailsModal from '../../components/resources/ResourceDetailsModal';

interface ModalState {
  resource: Resource | null;
  showPaymentInfo: boolean;
}

const ResourcesPage: React.FC = () => {
  const [filter, setFilter] = useState<string>('Todos');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [modalState, setModalState] = useState<ModalState>({ resource: null, showPaymentInfo: false });

  const openResourceDetails = (resource: Resource) => {
    setModalState({ resource, showPaymentInfo: false });
  };

  const openResourcePayment = (resource: Resource) => {
    setModalState({ resource, showPaymentInfo: true });
  };

  const closeModal = () => {
    setModalState({ resource: null, showPaymentInfo: false });
  };

  const filteredResources = resources.filter(resource => {
    const matchesFilter = filter === 'Todos' || (filter === 'Promos' ? !!resource.isKit : resource.type.includes(filter as ResourceType));
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const promosExist = resources.some(r => r.isKit);
  const allTypes = Array.from(new Set(resources.flatMap(r => r.type))).filter(t => t !== 'Audio');
  const uniqueTypes = ['Todos', ...(promosExist ? ['Promos'] : []), ...allTypes];

  return (
    <Layout>
      {/* Hero Section with Banner Style */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-r from-contessa-600 via-contessa-500 to-contessa-600 text-white">
        {/* Subtle Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center p-2 mb-6 bg-white/20 backdrop-blur-sm rounded-full px-5 border border-white/30 shadow-lg">
            <span className="text-sm font-medium tracking-wide uppercase">Tienda Oficial</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 text-white drop-shadow-sm">
            Recursos para tu Maternidad
          </h1>
          
          <p className="text-xl md:text-2xl opacity-95 max-w-3xl mx-auto font-light leading-relaxed">
            Herramientas creadas con amor, conocimiento y experiencia para acompañarte en cada etapa.
          </p>
        </div>
      </section>

      <section id="resources" className="py-16 md:py-20 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 -mt-24 relative z-20">
          
          {/* Filters and Search Bar Container */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-12 border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              
              {/* Category Pills */}
              <div className="flex items-center w-full lg:w-auto overflow-x-auto pt-1 pb-4 lg:pb-1 px-1 scrollbar-hide mask-fade-right">
                <Filter size={20} className="text-contessa-500 mr-4 shrink-0" />
                <div className="flex space-x-3">
                  {uniqueTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setFilter(type)}
                      className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap shadow-sm hover:shadow-md ${
                        filter === type 
                          ? 'bg-contessa-600 text-white ring-2 ring-contessa-600 ring-offset-2' 
                          : 'bg-gray-50 text-gray-600 hover:bg-contessa-50 hover:text-contessa-700 border border-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative w-full lg:w-80 group">
                <input
                  type="text"
                  placeholder="Buscar recursos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-contessa-50/50 border border-contessa-100 rounded-full focus:outline-none focus:ring-2 focus:ring-contessa-300 focus:bg-white transition-all shadow-inner text-contessa-900 placeholder-contessa-400"
                />
                <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-contessa-400 group-focus-within:text-contessa-500 transition-colors" />
              </div>
            </div>
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => (
                <ResourceCard 
                  key={resource.id} 
                  resource={resource} 
                  className="h-full" 
                  onClick={() => openResourceDetails(resource)}
                  onBuyClick={() => openResourcePayment(resource)}
                />
              ))
            ) : (
              <div className="col-span-full py-20 text-center bg-white rounded-3xl shadow-sm border border-gray-100">
                <div className="bg-contessa-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShoppingBag size={32} className="text-contessa-300" />
                </div>
                <h3 className="text-xl font-bold text-contessa-900 mb-2">No encontramos lo que buscas</h3>
                <p className="text-contessa-700/70 mb-6">Intenta con otros términos o cambia los filtros.</p>
                <button 
                  onClick={() => {setFilter('Todos'); setSearchTerm('');}}
                  className="px-6 py-2 bg-contessa-100 text-contessa-700 rounded-full font-semibold hover:bg-contessa-200 transition-colors"
                >
                  Ver todos los recursos
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <ResourceDetailsModal 
        resource={modalState.resource} 
        isOpen={!!modalState.resource} 
        onClose={closeModal}
        initialShowPayment={modalState.showPaymentInfo}
      />
    </Layout>
  );
};

export default ResourcesPage;
